import type { AuditEvent, Identity, PublisherProfile, PublisherStatus, Room, RoomInvite, RoomMember, RoomMemberRole, RoomSnapshot, UserSummary } from './types';

export class ApiClient {
  private accessToken = '';
  private accessExpiresAt = 0;
  private refreshInFlight?: Promise<{ access_token: string; expires_at: number }>;
  setToken(token: string, expiresAt = 0) { this.accessToken = token; this.accessExpiresAt = expiresAt; }
  clearToken() { this.accessToken = ''; this.accessExpiresAt = 0; }
  private async request<T>(path: string, init: RequestInit = {}, retryAfterRefresh = true): Promise<T> {
    const isAuthRequest = path === '/auth/login' || path === '/auth/refresh';
    if (this.accessToken && !isAuthRequest && retryAfterRefresh && this.accessExpiresAt > 0 &&
        Date.now() >= (this.accessExpiresAt - 30) * 1000) {
      await this.refresh();
    }
    const headers = new Headers(init.headers);
    if (this.accessToken) headers.set('Authorization', `Bearer ${this.accessToken}`);
    if (init.body) headers.set('Content-Type', 'application/json');
    const response = await fetch(`/api/v1${path}`, { ...init, headers, credentials: 'same-origin' });
    if (response.status === 401 && retryAfterRefresh && path !== '/auth/login' && path !== '/auth/refresh') {
      await this.refresh();
      return this.request<T>(path, init, false);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: { code: 'request_failed' } })) as { error?: { code?: string } };
      throw new Error(body.error?.code ?? 'request_failed');
    }
    return response.json() as Promise<T>;
  }
  async login(username: string, password: string) {
    const result = await this.request<{ access_token: string; expires_at: number }>('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) });
    this.setToken(result.access_token, result.expires_at); return result;
  }
  async refresh() {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.request<{ access_token: string; expires_at: number }>('/auth/refresh', { method: 'POST' }, false)
        .then(value => { this.setToken(value.access_token, value.expires_at); return value; })
        .finally(() => { this.refreshInFlight = undefined; });
    }
    return this.refreshInFlight;
  }
  async logout() { await this.request('/auth/logout', { method: 'POST' }); this.clearToken(); }
  me = () => this.request<Identity>('/me');
  hosting = () => this.request<import('./types').HostingStatus>('/hosting');
  hostRoom = (room_id: string, expected_room_id: string) => this.request<{ ok: boolean; room_id: string }>('/hosting', { method: 'POST', body: JSON.stringify({ room_id, expected_room_id, confirm: true }) });
  rooms = () => this.request<Room[]>('/rooms');
  roomState = (roomId: string) => this.request<RoomSnapshot>(`/room/state?room_id=${encodeURIComponent(roomId)}`);
  createRoom = (value: object) => this.request<{ id: string; member_role: RoomMemberRole }>('/rooms', { method: 'POST', body: JSON.stringify(value) });
  updateRoom = (id: string, value: object) => this.request(`/rooms/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(value) });
  roomMembers = (id: string) => this.request<RoomMember[]>(`/rooms/${encodeURIComponent(id)}/members`);
  setRoomMember = (id: string, username: string, member_role: RoomMemberRole) => this.request(`/rooms/${encodeURIComponent(id)}/members`, { method: 'PUT', body: JSON.stringify({ username, member_role }) });
  removeRoomMember = (id: string, username: string) => this.request(`/rooms/${encodeURIComponent(id)}/members`, { method: 'DELETE', body: JSON.stringify({ username }) });
  createRoomInvite = (id: string, max_uses: number) => this.request<RoomInvite>(`/rooms/${encodeURIComponent(id)}/invite-codes`, { method: 'POST', body: JSON.stringify({ max_uses, expires_in_hours: 168 }) });
  users = () => this.request<{ items: UserSummary[] }>('/accounts');
  profiles = (roomId: string) => this.request<{ items: PublisherProfile[] }>(`/publisher-profiles?room_id=${encodeURIComponent(roomId)}`);
  audits = (limit = 50, offset = 0) => this.request<{ items: AuditEvent[] }>(`/audit-events?limit=${limit}&offset=${offset}`);
  createUser = (value: object) => this.request('/accounts', { method: 'POST', body: JSON.stringify(value) });
  setUserState = (user_id: number, enabled: boolean) => this.request('/accounts/state', { method: 'PUT', body: JSON.stringify({ user_id, enabled }) });
  setUserRole = (user_id: number, role: string) => this.request('/accounts/role', { method: 'PUT', body: JSON.stringify({ user_id, role }) });
  saveProfile = (value: object) => this.request('/publisher-profiles', { method: 'PUT', body: JSON.stringify(value) });
  deleteProfile = (roomId: string, id: string) => this.request(`/publisher-profiles/${encodeURIComponent(id)}?room_id=${encodeURIComponent(roomId)}`, { method: 'DELETE' });
  publisherStatus = (roomId: string) => this.request<PublisherStatus>(`/publisher/status?room_id=${encodeURIComponent(roomId)}`);
  startPublisher = (room_id: string, profile_id: string) => this.request<PublisherStatus>('/publisher/start', { method: 'POST', body: JSON.stringify({ room_id, profile_id }) });
  stopPublisher = (room_id: string) => this.request<PublisherStatus>('/publisher/stop', { method: 'POST', body: JSON.stringify({ room_id }) });
}

export const api = new ApiClient();
